import { Literal, Static, Union } from "runtypes";
import { z } from "zod";

import { ExtractAtomicOperationFromDefinition } from "../../atomic-operations/AtomicOperationDefinition.js";
import { keyValueUpdatePayloadSchema } from "../../atomic-operations/keyValueUpdateSchema.js";
import { HexId, MagicEventId, UserId } from "../../idTypeBrands.js";
import {
  ExploreIntent,
  MagicAcceptType,
  MagicCellPayload,
  MagicCellRunResult,
  MagicClientReferenceInfo,
  MagicEventFailureReason,
  MagicEventStatus,
  MagicKeyword,
  MagicRequestMode,
} from "../../MagicTypes.js";
import { getNormalEnum } from "../../runtypeEnums.js";
import { SemanticCap } from "../../semanticCaps.js";
import { typedObjectKeys } from "../../utils/typedObjects.js";
import { createHexVersionAtomicOperationDefinition } from "../HexVersionAtomicOperationDefinition.js";

import { CREATE_MAGIC_EVENT } from "./createMagicEventOperation.js";
import { zHexId, zMagicEventId, zUserId } from "../../zod/ZodIdTypeBrands.js";
import { zFromRuntype, zReadonlyArray } from "../../zod/utils.js";

export const MagicEventDerivedFieldsLiteral = Union(
  Literal("requestMode"),
  Literal("relatedProjectIds"),
  Literal("topReferenceInfo"),
  Literal("summary"),
  Literal("intent"),
);

export type MagicEventDerivedFields = Static<
  typeof MagicEventDerivedFieldsLiteral
>;
export const MagicEventDerivedFields = getNormalEnum(
  MagicEventDerivedFieldsLiteral,
);

export interface MagicEventUpdatableFields {
  status: MagicEventStatus | null;
  accepted: boolean | null;
  nextEventId: MagicEventId | null;
  prevEventId: MagicEventId | null;
  requestMode: MagicRequestMode | null;
  unsupportedResponse: boolean;
  cellRunResult: MagicCellRunResult | null;
  userFacingResult: string | null;
  acceptType: MagicAcceptType | null;
  failureReason: MagicEventFailureReason | null;
  result: string | null;
  autoTriggered: boolean;
  cellPayload: MagicCellPayload | null;
  relatedProjectIds: readonly HexId[] | null;
  topReferenceInfo: readonly MagicClientReferenceInfo[] | null;
  summary: string | null;
  intent: ExploreIntent | null;
  cancelled: boolean;
  eventType: MagicKeyword | null;
}

const _UpdateMagicEventSafeFields: {
  [key in keyof MagicEventUpdatableFields]: "";
} = {
  status: "",
  accepted: "",
  nextEventId: "",
  prevEventId: "",
  requestMode: "",
  unsupportedResponse: "",
  cellRunResult: "",
  userFacingResult: "",
  acceptType: "",
  failureReason: "",
  result: "",
  autoTriggered: "",
  cellPayload: "",
  relatedProjectIds: "",
  topReferenceInfo: "",
  summary: "",
  cancelled: "",
  intent: "",
  eventType: "",
};

export const UpdateMagicEventSafeFields = typedObjectKeys(
  _UpdateMagicEventSafeFields,
);

const UPDATE_MAGIC_EVENT_TYPE = "UPDATE_MAGIC_EVENT" as const;
export const UpdateMagicEventPayload = keyValueUpdatePayloadSchema({
  shared: {
    magicEventId: zMagicEventId,
    eventType: z.nativeEnum(MagicKeyword).nullable(),
    userId: zUserId.nullable(),
  },
  fields: {
    status: z.nativeEnum(MagicEventStatus).nullable(),
    accepted: z.boolean().nullable(),
    nextEventId: zMagicEventId.nullable(),
    prevEventId: zMagicEventId.nullable(),
    requestMode: z.nativeEnum(MagicRequestMode).nullable(),
    unsupportedResponse: z.boolean(),
    cellRunResult: z.nativeEnum(MagicCellRunResult).nullable(),
    userFacingResult: z.string().nullable(),
    acceptType: z.nativeEnum(MagicAcceptType).nullable(),
    failureReason: z.nativeEnum(MagicEventFailureReason).nullable(),
    result: z.string().nullable(),
    autoTriggered: z.boolean(),
    cellPayload: zFromRuntype(MagicCellPayload).nullable(),
    relatedProjectIds: zReadonlyArray(zHexId).nullable(),
    topReferenceInfo: zReadonlyArray(
      zFromRuntype(MagicClientReferenceInfo),
    ).nullable(),
    summary: z.string().nullable(),
    intent: z.nativeEnum(ExploreIntent).nullable(),
    cancelled: z.boolean(),
    eventType: z.nativeEnum(MagicKeyword).nullable(),
  } satisfies Record<keyof MagicEventUpdatableFields, z.ZodTypeAny>,
});
export type UpdateMagicEventPayload = z.infer<typeof UpdateMagicEventPayload>;

export const UPDATE_MAGIC_EVENT = createHexVersionAtomicOperationDefinition({
  type: UPDATE_MAGIC_EVENT_TYPE,
  payloadSchema: UpdateMagicEventPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    idArg: "magicEventId",
    idType: "MagicEvent",
    cap: SemanticCap.EDIT_MAGIC_EVENT,
  },
  scopedIds: {
    userId: {
      scope: "external",
      reason: "A user belongs to the workspace, not to any project.",
    },
    magicEventId: { scope: "ownedByWrapper", idType: "MagicEvent" },
  },
  logSafe: ["magicEventId", "key"],
  conflictId: (op) =>
    `${UPDATE_MAGIC_EVENT_TYPE}-${op.payload.magicEventId}-${op.payload.key}`,
  creationId: (op) => `${CREATE_MAGIC_EVENT.type}-${op.payload.magicEventId}`,
  excludeFromHistory: true,
  alwaysSkipUndoBuffer: true,
  create: <K extends keyof MagicEventUpdatableFields>(
    magicEventId: MagicEventId,
    key: K,
    value: MagicEventUpdatableFields[K],
    metadata?: {
      eventType?: MagicKeyword | null;
      userId?: UserId | null;
    },
  ) => ({
    type: UPDATE_MAGIC_EVENT_TYPE,
    payload: {
      magicEventId,
      key,
      value,
      // Used for filtering notebook chat magic events
      eventType: metadata?.eventType ?? null,
      userId: metadata?.userId ?? null,
    },
  }),
});

export type UPDATE_MAGIC_EVENT = ExtractAtomicOperationFromDefinition<
  typeof UPDATE_MAGIC_EVENT
>;
